/** Imports ---------------------------------------------------------------- */

/** React imports */
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import PropTypes from "prop-types";

/** Icons */
import { ReactComponent as CloudUploadIcon } from "../../../assets/images/CloudUploadIcon.svg";

/** MUI Icons */
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

/** MUI imports */
import { styled } from "@mui/material/styles";
import GridUI from "@mui/material/Grid";
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    LinearProgress,
    List,
    ListSubheader,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    DialogTitle,
    Divider,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TablePagination,
    Paper,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";

/** Component imports */
import Backdrop from "../Backdrop";
import moment from "moment";
import DialogAlerts from "../DialogAlerts";

const PREFIX = "UploadAsyncCustom";

const classes = {
    dropzone: `${PREFIX}-dropzone`,
    dropzoneDisabled: `${PREFIX}-dropzoneDisabled`,
};

const CustomFormControlLabel = styled(FormControlLabel)(() => ({
    "& .MuiFormControlLabel-label": {
        fontWeight: "bold",
    },
}));

const ColorButton = styled(Button)(({ theme }) => ({
    border: "1px solid #ff5968",
    borderRadius: 40,
}));

const CustomDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(4),
    },
    "& .MuiDialogTitle-root": {
        backgroundColor: "#031851",
        fontSize: "18px",
        color: "#fff",
    },
    "& .MuiDialog-paper": {
        borderRadius: 8,
        boxShadow: "10px 10px 15px rgba(3,24,81,0.15)",
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2),
    },

    [`& .${classes.dropzone}`]: {
        flex: 1,
        display: "flex",
        padding: "64px 24px",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "rgba(47,49,144,0.1)",
        border: "2px dashed #C1C1DE",
        borderRadius: "16px",
        "&:hover": {
            border: "2px dashed #2f3190",
        },
    },
    [`& .${classes.dropzoneDisabled}`]: {
        flex: 1,
        display: "flex",
        padding: "64px 24px",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "rgba(47,49,144,0.1)",
        border: "2px dashed #C1C1DE",
        borderRadius: "16px",
    },
}));

const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "rgba(19, 31, 62, 0.80)",
        fontSize: 12,
        maxWidth: 320,
        borderRadius: 6,
        fontWeight: "normal",
    },
});

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: "flex", mt: 2, alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress
                    variant="determinate"
                    {...props}
                    sx={{
                        height: 10,
                        borderRadius: 5,
                    }}
                />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}

LinearProgressWithLabel.propTypes = {
    /**
     * The value of the progress indicator for the determinate and buffer variants.
     * Value between 0 and 100.
     */
    value: PropTypes.number.isRequired,
};

const UploadAsyncCustom = (props) => {
    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /* React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [showBackdrop, setBackDrop] = useState(false);
    const [progressChecks, setProgressChecks] = useState(0);
    const [openAlertDelete, setOpenAlertDelete] = useState(false);
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;
    const limit_mb = props.customRequirementInfo.req_type.toString() === "5" ? " 15 MB." : " 5 MB.";

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const meses = [
        t("monthNames.january"),
        t("monthNames.february"),
        t("monthNames.march"),
        t("monthNames.april"),
        t("monthNames.may"),
        t("monthNames.june"),
        t("monthNames.july"),
        t("monthNames.august"),
        t("monthNames.september"),
        t("monthNames.october"),
        t("monthNames.november"),
        t("monthNames.december"),
    ];

    /** Global variables */
    const teamDelete = [
        "kgarcia@gca.digital",
        "dloyo@gca.digital",
        "gloyo@gca.digital",
        "dleon@gca.digital",
        "iguevara@gca.digital",
        "galbani@gca.digital",
        "jalvarez@gca.digital",
        "btorres@gca.digital",
        "jrojas@gca.digital",
        "mmedina@gca.digital",
    ];

    /** Component functions */
    const formatBytes = (bytes, decimals = 0) => {
        if (bytes === 0) return "0 Bytes";

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    const onDropChecks = useCallback(
        async (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                let folder_name =
                    props.customRequirementInfo.req_type.toString() === "1"
                        ? "Reporte de nómina"
                        : props.customRequirementInfo.req_type.toString() === "2"
                        ? "Reporte de contratistas"
                        : props.customRequirementInfo.req_type.toString() === "3"
                        ? "Reporte de ventas"
                        : props.customRequirementInfo.req_type.toString() === "4"
                        ? "Declaración de ISLR"
                        : props.customRequirementInfo.req_name;

                let params = {
                    id_organisation: props.customRequirementInfo.idOrganisation,
                    month: props.customRequirementInfo.month,
                    year: props.selectedYear,
                    CustomRequirement: folder_name,
                };
                await axios
                    .get("/getTotalSizeFileS3", { params })
                    .then(async (response) => {
                        let finalSize = 0;
                        acceptedFiles.forEach((item) => {
                            finalSize += item.size;
                        });

                        const limit_size = limit_mb === " 15 MB." ? 15000000 : 5000000;
                        if (parseInt(response.data.size + finalSize) < limit_size) {
                            let data = new FormData();
                            data.append("id_organisation", props.customRequirementInfo.idOrganisation);
                            data.append("month", props.customRequirementInfo.month);
                            data.append("year", props.selectedYear);
                            if (acceptedFiles.length > 1) {
                                for (var x = 0; x < acceptedFiles.length; x++) {
                                    data.append("files", acceptedFiles[x]);
                                }
                            } else {
                                data.append("file", acceptedFiles[0]);
                            }
                            data.append("CustomRequirement", folder_name);
                            data.append("custom_req_type", props.customRequirementInfo.req_type);
                            data.append("custom_requirement_id", props.customRequirementInfo.custom_requirement_id);

                            const options = {
                                onUploadProgress: (progressEvent) => {
                                    const { loaded, total } = progressEvent;
                                    let percent = Math.floor((loaded / total) * 100);

                                    if (percent < 100) {
                                        setProgressChecks(percent);
                                    }
                                },
                            };

                            let urlApi = "";

                            if (acceptedFiles.length > 1) {
                                urlApi = "/uploadFileS3multiple";
                            } else {
                                urlApi = "/uploadFileS3";
                            }

                            await axios
                                .post(urlApi, data, options)
                                .then(() => {
                                    setProgressChecks(0);
                                    props.setFilesDataCustom(props.filesDataCustom.concat([...acceptedFiles]));
                                    props.loadInitData();
                                })
                                .catch((err) => {
                                    console.log(err);
                                });
                        } else {
                            props.setTexto(t("dialogConversions.maximunUploadSizeMonthly") + limit_mb);
                            props.setAlertType("warning");
                            props.setShow(true);
                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            } else {
                props.setTexto(t("dialogConversions.maximunUploadSize"));
                props.setAlertType("warning");
                props.setShow(true);
            }
        },
        [limit_mb, props, t]
    );

    const onDropRejectedChecks = useCallback(
        (fileRejections) => {
            props.setFilesRejectedCustom([...props.filesRejectedCustom, ...fileRejections]);
        },
        [props]
    );

    const { getRootProps: getRootChecksProps, getInputProps: getInputChecksProps } = useDropzone({
        disabled: props.customStatus,
        noKeyboard: true,
        maxSize: limit_mb === " 15 MB." ? 15000000 : 5000000,
        //maxFiles: 1,
        multiple: true,
        onDrop: onDropChecks,
        onDropRejected: onDropRejectedChecks,
        accept: {
            "image/jpeg": [],
            "image/jpg": [],
            "image/png": [],
            "application/pdf": [],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
            "text/csv": [".csv"],
        },
    });

    const removeFile = (file) => async () => {
        setBackDrop(true);
        let params = {};

        let folder_name =
            props.customRequirementInfo.req_type.toString() === "1"
                ? "Reporte de nómina"
                : props.customRequirementInfo.req_type.toString() === "2"
                ? "Reporte de contratistas"
                : props.customRequirementInfo.req_type.toString() === "3"
                ? "Reporte de ventas"
                : props.customRequirementInfo.req_type.toString() === "4"
                ? "Declaración de ISLR"
                : props.customRequirementInfo.req_name;

        if (props.uploadedFilesCustom.length === 0) {
            params = {
                id_organisation: props.customRequirementInfo.idOrganisation,
                month: props.customRequirementInfo.month,
                year: props.selectedYear,
                file_name: file.name,
                CustomRequirement: folder_name,
                custom_req_type: props.customRequirementInfo.req_type,
                custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
            };

            await axios
                .post("/deleteFileS3", params)
                .then(() => {
                    const newFiles = [...props.filesDataCustom];
                    newFiles.splice(newFiles.indexOf(file), 1);
                    props.setFilesDataCustom(newFiles);
                    props.loadInitData();
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            params = {
                id_organisation: props.customRequirementInfo.idOrganisation,
                month: props.customRequirementInfo.month,
                year: props.selectedYear,
                file_name: file,
                CustomRequirement: folder_name,
                custom_req_type: props.customRequirementInfo.req_type,
                custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
            };

            await axios
                .post("/deleteFileS3", params)
                .then(() => {
                    props.onClose();
                    props.loadInitData();
                    props.setTexto(t("dialogConversions.filesRemoved"));
                    props.setAlertType("success");
                    props.setShow(true);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
        setBackDrop(false);
    };

    const downloadFile = (file) => async () => {
        let params = {};

        let folder_name =
            props.customRequirementInfo.req_type.toString() === "1"
                ? "Reporte de nómina"
                : props.customRequirementInfo.req_type.toString() === "2"
                ? "Reporte de contratistas"
                : props.customRequirementInfo.req_type.toString() === "3"
                ? "Reporte de ventas"
                : props.customRequirementInfo.req_type.toString() === "4"
                ? "Declaración de ISLR"
                : props.customRequirementInfo.req_name;
        if (props.uploadedFilesCustom.length === 0) {
            params = {
                id_organisation: props.customRequirementInfo.idOrganisation,
                month: props.customRequirementInfo.month,
                year: props.selectedYear,
                file_name: file.name,
                CustomRequirement: folder_name,
                custom_req_type: props.customRequirementInfo.req_type,
                custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
            };
        } else {
            params = {
                id_organisation: props.customRequirementInfo.idOrganisation,
                month: props.customRequirementInfo.month,
                year: props.selectedYear,
                file_name: file,
                CustomRequirement: folder_name,
                custom_req_type: props.customRequirementInfo.req_type,
                custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
            };
        }

        await axios
            .get(`/getFileS3`, { params, responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", response.config.params.file_name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const downloadAll = async () => {
        let folder_name =
            props.customRequirementInfo.req_type.toString() === "1"
                ? "Reporte de nómina"
                : props.customRequirementInfo.req_type.toString() === "2"
                ? "Reporte de contratistas"
                : props.customRequirementInfo.req_type.toString() === "3"
                ? "Reporte de ventas"
                : props.customRequirementInfo.req_type.toString() === "4"
                ? "Declaración de ISLR"
                : props.customRequirementInfo.req_name;

        let params = {};
        params = {
            id_organisation: props.customRequirementInfo.idOrganisation,
            month: props.customRequirementInfo.month,
            year: props.selectedYear,
            CustomRequirement: folder_name,
            custom_req_type: props.customRequirementInfo.req_type,
            custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
        };

        await axios
            .get(`/getBulkFilesS3`, { params, responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;

                let nameZip =
                    props.customRequirementInfo.company +
                    " - " +
                    folder_name +
                    " - " +
                    props.selectedYear +
                    props.customRequirementInfo.month +
                    ".zip";
                link.setAttribute("download", nameZip);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const deleteAll = () => async () => {
        let folder_name =
            props.customRequirementInfo.req_type.toString() === "1"
                ? "Reporte de nómina"
                : props.customRequirementInfo.req_type.toString() === "2"
                ? "Reporte de contratistas"
                : props.customRequirementInfo.req_type.toString() === "3"
                ? "Reporte de ventas"
                : props.customRequirementInfo.req_type.toString() === "4"
                ? "Declaración de ISLR"
                : props.customRequirementInfo.req_name;
        setOpenAlertDelete(false);
        setBackDrop(true);
        let params = {};

        if (props.uploadedFilesCustom.length === 0) {
            params = {
                id_organisation: props.customRequirementInfo.idOrganisation,
                month: props.customRequirementInfo.month,
                year: props.selectedYear,
                CustomRequirement: folder_name,
                custom_req_type: props.customRequirementInfo.req_type,
                custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
            };

            await axios
                .post("/deleteBulkFilesS3", params)
                .then(() => {
                    props.setFilesDataCustom([]);
                    props.loadInitData();
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            params = {
                id_organisation: props.customRequirementInfo.idOrganisation,
                month: props.customRequirementInfo.month,
                year: props.selectedYear,
                CustomRequirement: folder_name,
                custom_req_type: props.customRequirementInfo.req_type,
                custom_requirement_id: props.customRequirementInfo.custom_requirement_id,
            };

            await axios
                .post("/deleteBulkFilesS3", params)
                .then(() => {
                    props.onClose();
                    props.loadInitData();
                    props.setTexto(t("dialogConversions.filesRemoved"));
                    props.setAlertType("success");
                    props.setShow(true);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
        setBackDrop(false);
    };

    const acceptedFileItems = props.filesDataCustom.map((file) => (
        <ListItem
            disablePadding
            secondaryAction={
                <Box>
                    <LightTooltip title={t("dialogConversions.download")} aria-label="downloadFile">
                        <IconButton edge="end" aria-label="delete" onClick={downloadFile(file)} sx={{ mr: 1 }}>
                            <FileDownloadIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                    <LightTooltip title={t("dialogConversions.remove")} aria-label="removeFile">
                        <IconButton edge="end" aria-label="delete" onClick={removeFile(file)}>
                            <DeleteIcon fontSize="medium" color="primary" />
                        </IconButton>
                    </LightTooltip>
                </Box>
            }
            sx={{ height: 50 }}
        >
            <ListItemIcon sx={{ minWidth: "20px" }}>
                <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
            </ListItemIcon>
            <ListItemText
                key={file.path}
                primary={file.name}
                secondary={formatBytes(file.size)}
                secondaryTypographyProps={{ fontSize: "12px" }}
            />
        </ListItem>
    ));

    const fileRejectionItems = useMemo(
        () =>
            props.filesRejectedCustom.map(({ file }) => (
                <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                        <DescriptionOutlinedIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
                    </ListItemIcon>
                    <ListItemText
                        key={file.path}
                        primary={file.name}
                        secondary={formatBytes(file.size)}
                        secondaryTypographyProps={{ fontSize: "12px" }}
                        sx={{ color: "#ff5968" }}
                    />
                </ListItem>
            )),
        [props.filesRejectedCustom]
    );

    const uploadedFilesItems2 = (files) => {
        let data = files;
        return (
            <>
                <Paper sx={{ boxShadow: 0 }}>
                    <TableContainer>
                        <Table aria-label="simple table">
                            <TableBody>
                                {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((file, index) => (
                                    <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                        <TableCell align="left" sx={{ p: 0, border: 0, display: "flex" }}>
                                            <DescriptionOutlinedIcon fontSize="medium" color="primary" sx={{ mr: 1 }} />
                                            <Typography>
                                                {file}&nbsp;&nbsp;({props.uploadedFilesCustomSizes[index]})&nbsp;&nbsp;
                                                <b>{props.uploadedFilesCustomUploadDate[index] ? "(" + moment.utc(props.uploadedFilesCustomUploadDate[index]).format(todos.dateFormat) + ")" : ""}</b>
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 0, border: 0, whiteSpace: "nowrap" }}>
                                            <Box>
                                                <LightTooltip
                                                    title={t("dialogConversions.download")}
                                                    aria-label="downloadFile"
                                                >
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        onClick={downloadFile(file)}
                                                        sx={{
                                                            mr: "-12px",
                                                            ...((props.customRequirementInfo.month ===
                                                                moment().subtract(1, "months").format("MM") ||
                                                                (teamDelete.includes(todos.userInfo.email) &&
                                                                    props.customRequirementInfo.month !==
                                                                        moment()
                                                                            .subtract(1, "months")
                                                                            .format("MM"))) && {
                                                                mr: 1,
                                                            }),
                                                        }}
                                                    >
                                                        <FileDownloadIcon fontSize="medium" color="primary" />
                                                    </IconButton>
                                                </LightTooltip>
                                                {props.customRequirementInfo.month ===
                                                    moment().subtract(1, "months").format("MM") ||
                                                (teamDelete.includes(todos.userInfo.email) &&
                                                    props.customRequirementInfo.month !==
                                                        moment().subtract(1, "months").format("MM")) ? (
                                                    <LightTooltip
                                                        title={t("dialogConversions.remove")}
                                                        aria-label="removeUploadedFile"
                                                    >
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="delete"
                                                            onClick={removeFile(file)}
                                                        >
                                                            <DeleteIcon fontSize="medium" color="primary" />
                                                        </IconButton>
                                                    </LightTooltip>
                                                ) : null}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        shape="rounded"
                        rowsPerPageOptions={[10]}
                        component="div"
                        count={files.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("miscellaneous.of")} ${count}`}
                    />
                </Paper>
            </>
        );
    };

    return (
        <>
            <DialogAlerts
                open={openAlertDelete}
                onClose={() => setOpenAlertDelete(false)}
                agreeBtnLabel={t("miscellaneous.accept")}
                disagreeBtnLabel={t("miscellaneous.cancel")}
                type={"info"}
                title={t("miscellaneous.filesSureDelete")}
                agreeAction={deleteAll()}
            />
            <CustomDialog
                open={props.open}
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        props.onClose();
                    }
                }}
                maxWidth="md"
                fullWidth={true}
            >
                <DialogTitle id="alert-dialog-title">
                    {props.customRequirementInfo.company} /{" "}
                    <span style={{ color: "#ff5968" }}>
                        {props.customRequirementInfo.req_type.toString() === "1"
                            ? t("miscellaneous.reportNom")
                            : props.customRequirementInfo.req_type.toString() === "2"
                            ? t("miscellaneous.reportCont")
                            : props.customRequirementInfo.req_type.toString() === "3"
                            ? t("miscellaneous.reportSells")
                            : props.customRequirementInfo.req_type.toString() === "4"
                            ? t("miscellaneous.reportISLR")
                            : props.customRequirementInfo.req_name}
                    </span>{" "}
                    / {meses[parseInt(props.customRequirementInfo.month) - 1]}
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        ...((props.filesDataCustom.length > 0 || props.uploadedFilesCustom.length > 0) && {
                            pb: "0 !important",
                        }),
                    }}
                >
                    <div
                        {...getRootChecksProps({
                            className: props.customStatus ? classes.dropzoneDisabled : classes.dropzone,
                        })}
                    >
                        <input {...getInputChecksProps()} />
                        <CloudUploadIcon fill={props.customStatus ? "#C1C1DE" : "#031851"} />
                        <Typography
                            variant="h1"
                            color={props.customStatus ? "#C1C1DE" : "#031851"}
                            sx={{ cursor: "default", pt: 3 }}
                        >
                            {t("dialogConversions.selectFile")}
                        </Typography>
                    </div>
                    <Typography variant="body1" color="primary" sx={{ pt: 1 }}>
                        {t("dialogConversions.validFormats")}{" "}
                        <Typography component="span" variant="h4" color="primary">
                            .pdf, .jpg, .jpeg, .pdf, .xls, .xlsx, .csv
                        </Typography>
                        <Typography variant="body1" color="primary">
                            {t("dialogConversions.maximunUploadSize") + ": " + limit_mb}
                        </Typography>
                        <Typography variant="body1" color="primary">
                            {t("dialogConversions.monthlyUploadLimit")}
                            {limit_mb}
                        </Typography>
                    </Typography>
                    {progressChecks > 0 && <LinearProgressWithLabel value={progressChecks} />}
                    {acceptedFileItems.length > 0 ? (
                        <List
                            component="nav"
                            aria-labelledby="acceptedFiles-list-subheader"
                            subheader={
                                <ListSubheader component="div" id="acceptedFiles-list-subheader" sx={{ p: 0 }}>
                                    <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                        {`${t("dialogConversions.uploadedFiles")}:`}
                                    </Typography>
                                </ListSubheader>
                            }
                        >
                            {acceptedFileItems}
                        </List>
                    ) : null}
                    {fileRejectionItems.length > 0 ? (
                        <List
                            component="nav"
                            aria-labelledby="fileRejections-list-subheader"
                            subheader={
                                <ListSubheader component="div" id="fileRejections-list-subheader" sx={{ p: 0 }}>
                                    <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                        {`${t("dialogConversions.rejectedFiles")}:`}
                                    </Typography>
                                </ListSubheader>
                            }
                        >
                            {fileRejectionItems}
                        </List>
                    ) : null}
                    {props.uploadedFilesCustom.length > 0 ? (
                        <List
                            component="nav"
                            aria-labelledby="uploadedFiles-list-subheader"
                            subheader={
                                <>
                                    <ListItem
                                        disablePadding
                                        secondaryAction={
                                            props.uploadedFilesCustom.length > 1 && (
                                                <Box>
                                                    <LightTooltip
                                                        title={t("dialogConversions.download")}
                                                        aria-label="downloadFile"
                                                    >
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="download"
                                                            onClick={downloadAll}
                                                            sx={{
                                                                mr: "-12px",
                                                                ...((props.customRequirementInfo.month ===
                                                                    moment().subtract(1, "months").format("MM") ||
                                                                    (teamDelete.includes(todos.userInfo.email) &&
                                                                        props.customRequirementInfo.month !==
                                                                            moment()
                                                                                .subtract(1, "months")
                                                                                .format("MM"))) && {
                                                                    mr: 1,
                                                                }),
                                                            }}
                                                        >
                                                            <FileDownloadIcon fontSize="medium" color="primary" />
                                                        </IconButton>
                                                    </LightTooltip>
                                                    {props.customRequirementInfo.month ===
                                                        moment().subtract(1, "months").format("MM") ||
                                                    (teamDelete.includes(todos.userInfo.email) &&
                                                        props.customRequirementInfo.month !==
                                                            moment().subtract(1, "months").format("MM")) ? (
                                                        <LightTooltip
                                                            title={t("dialogConversions.remove")}
                                                            aria-label="removeFile"
                                                        >
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="delete"
                                                                onClick={() => setOpenAlertDelete(true)}
                                                            >
                                                                <DeleteIcon fontSize="medium" color="primary" />
                                                            </IconButton>
                                                        </LightTooltip>
                                                    ) : null}
                                                </Box>
                                            )
                                        }
                                        sx={{ height: 50 }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="h4" color="primary" sx={{ pt: 3, pb: 1 }}>
                                                    {`${t("dialogConversions.repositoryFiles")} (${
                                                        props.uploadedFilesCustom.length
                                                    }):`}
                                                </Typography>
                                            }
                                            secondaryTypographyProps={{ fontSize: "12px" }}
                                        />
                                    </ListItem>

                                    <Divider sx={{ py: 0.5 }} />
                                </>
                            }
                        >
                            {uploadedFilesItems2(props.uploadedFilesCustom)}
                        </List>
                    ) : null}
                    {props.filesDataCustom.length + props.uploadedFilesCustom.length === 0 && (
                        <CustomFormControlLabel
                            control={
                                <Checkbox
                                    checked={props.customStatus}
                                    onChange={(e) => props.handleChangeCustomStatus(e)}
                                />
                            }
                            label={t("miscellaneous.notApplyMonth")}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: "16px 32px !important" }}>
                    <GridUI container justifyContent="space-between" alignItems="center" spacing={2}>
                        <GridUI item>
                            <ColorButton
                                sx={{
                                    mr: 1,
                                    "&:hover": {
                                        backgroundColor: "white",
                                        color: "#2f3190",
                                        border: "1px solid #2f3190",
                                    },
                                }}
                                color="secondary"
                                disableElevation
                                variant="outlined"
                                onClick={props.handleCloseDialogCustomDiscard}
                            >
                                {t("miscellaneous.cancel")}
                            </ColorButton>
                        </GridUI>
                        <GridUI item>
                            <ColorButton
                                disableElevation
                                variant="contained"
                                color="secondary"
                                onClick={props.onClose}
                                sx={{
                                    "&:hover": {
                                        backgroundColor: "#2f3190",
                                        border: "1px solid #2f3190",
                                        color: "#fff",
                                    },
                                    "&.MuiButton-root.Mui-disabled": {
                                        backgroundColor: "#ffcdd2 !important",
                                        border: "none",
                                        color: "#fff",
                                    },
                                }}
                            >
                                {t("miscellaneous.save")}
                            </ColorButton>
                        </GridUI>
                    </GridUI>
                </DialogActions>
                <Backdrop open={showBackdrop} />
            </CustomDialog>
        </>
    );
};

export default UploadAsyncCustom;
